import { Timestamp } from '@ms/yammer-protobuf';
export type { ClientContext } from './types/ClientContext';

export interface BaseTelemetryEvent {
  readonly type: TelemetryEventType;
  readonly name: string;
  readonly occurredAt: string;
  readonly skipAuthentication?: boolean;
}

export enum TelemetryEventType {
  Analytics = 'Analytics',
  AnalyticsV2 = 'AnalyticsV2',
  Error = 'Error',
  Info = 'Info',
  Performance = 'Performance',
}

export interface PropertyCheckEventProperties {
  readonly droppedKeys: string;
}

export type TelemetryPropertyValueType =
  | string
  | number
  | boolean
  | undefined
  | null
  | string[]
  | AnalyticsV2EventTimestamp
  | readonly PerformanceServerTiming[];

export type AnalyticsV2EventTimestamp =
  | {
      readonly seconds: string | number | null;
      readonly nanos: string | number | null;
    }
  | Timestamp;

export type TelemetryClientId =
  | 'UNKNOWN'
  | 'YAMMER_EMBED'
  | 'YAMMER_VIVA_CONNECTIONS'
  | 'YAMMER_METAOS_OUTLOOK_PERSONAL_APP'
  | 'YAMMER_METAOS_OFFICE_PERSONAL_APP'
  | 'YAMMER_OUTLOOK'
  | 'YAMMER_SHAREPOINT'
  | 'YAMMER_SHAREPOINT_SHARETO'
  | 'YAMMER_TEAMS'
  | 'YAMMER_TEAMS_MEETING'
  | 'YAMMER_TEAMS_BRANDED_EXPERIENCES_APP'
  | 'YAMMER_TEAMS_PERSONAL_APP'
  | 'YAMMER_VIVAENGAGE_PERSONAL_APP'
  | 'YAMMER_VIVAENGAGE_OUTLOOK_PERSONAL_APP'
  | 'YAMMER_VIVAENGAGE_OFFICE_PERSONAL_APP'
  | 'YAMMER_WEB';

export interface ClientSuppliedConfig {
  readonly yammerAppClientId: string;
  readonly telemetryClientId: TelemetryClientId;
  readonly gitRevision: string;
  readonly batchSizeInBytes: number;
  readonly eventReportIntervalInMilliseconds: number;
  readonly reportAuthorizationReceiver: string;
  readonly reportAuthorizationFallbacks: string[];
}

export interface ClientConfig extends ClientSuppliedConfig {
  readonly appInstanceId: string;
  readonly loadedAt: number;
  readonly resource: string;
  readonly graphqlHost: string;
  readonly telemetryHost: string;
  readonly shouldLogProtobufEventsToConsole: boolean;
}

export type HostingEnvironment =
  | 'dev'
  | 'edog'
  | 'MSIT'
  | 'prodbubble'
  | 'PROD'
  | 'SDFV2'
  | 'Dogfood'
  | 'dogfood'
  | 'Microsoft'
  | 'FirstRelease'
  | 'WW';

export interface HostingConfig extends Record<string, unknown> {
  /**
   * A specific partition of substrate servers in a particular region
   */
  readonly forest?: string;
  /**
   * The environment from which the assets are being served. This
   * could be the deployment ring that the user is a part of or the local
   * development environment (e.g. prod, sdfv2, dev)
   */
  readonly hostingEnvironment: HostingEnvironment;
  /**
   * The host from which the app is loaded.
   */
  readonly hostname?: string;
  /**
   * The path to the CDN hosting the script assets
   */
  readonly scriptBaseUrl?: string;
  /**
   * revision: the current build version. Format: $(date:yyyyMMdd)$(rev:rrr)
   */
  readonly scriptRevision: string;
  /**
   * The specific name of the server that served the html page
   */
  readonly server?: string;
  /**
   * The version of the server
   */
  readonly serverPageVer?: string;
  /**
   * The path to the static assets for loading the page
   * (e.g. service worker, favicon)
   */
  readonly staticBaseUrl?: string;
  /**
   * A unique value for tracking a user across the same browser across time
   */
  readonly userBrowserIdentity?: string;
  /**
   * A unique value for yammer-sharepoint webpart to track it across sharepoint and yammer
   */
  readonly webPartInstanceId?: string;
  /**
   * A unique value for tracking yammer-sharepoint webpart request to sharepoint server
   *
   */
  readonly webPartCorrelationId?: string;
}

export type PageName =
  | 'AdminCopilotAdoption'
  | 'AdminHome'
  | 'AdminSettingsStoryline'
  | 'AdminSettingsStorylineNotifications'
  | 'AMAHome'
  | 'AMACreate'
  | 'AMAEdit'
  | 'AMAEvent'
  | 'AMAEventDismissed'
  | 'AMAEventInReview'
  | 'AMAEventPublished'
  | 'AMAGroupEvents'
  | 'AMALeadersEvents'
  | 'AMAUserEvents'
  | 'Answers'
  | 'Article'
  | 'ArticleEdit'
  | 'ArticleNew'
  | 'AttachableLink'
  | 'Campaign'
  | 'CampaignLegacyRedirect'
  | 'CampaignsDashboard'
  | 'CampaignsDashboardWithSubroute'
  | 'CampaignsHome'
  | 'CuratedThreads'
  | 'Configure'
  | 'ConversationAnalytics'
  | 'GroupActivity'
  | 'GroupCampaigns'
  | 'GroupCreate'
  | 'GroupCreateLegacyRedirect'
  | 'GroupDelete'
  | 'GroupDiscoverLegacyRedirect'
  | 'GroupEvents'
  | 'GroupFeed'
  | 'GroupFeedWithSubroute'
  | 'GroupFiles'
  | 'GroupInfo'
  | 'GroupInfoLegacyRedirect'
  | 'GroupInsights'
  | 'GroupInsightsLegacy'
  | 'GroupSettings'
  | 'GroupTopQuestions'
  | 'GroupsAll'
  | 'GroupsDiscover'
  | 'GroupsDiscoverWithSubroute'
  | 'GroupsExplore'
  | 'GroupsMine'
  | 'HomeFeed'
  | 'HomeRedirect'
  | 'Inbox'
  | 'InboxWithSubroute'
  | 'Insights'
  | 'LeaderDashboard'
  | 'Leaders'
  | 'LeadersWithSubroute'
  | 'LeadersAll'
  | 'LeadersConnected'
  | 'LeadersCuratedFeed'
  | 'LeadersFollowed'
  | 'LeaderManageLegacyRedirect'
  | 'LeadersManageLegacyRedirect'
  | 'Leader'
  | 'LeadershipCorner'
  | 'LegacyRedirect'
  | 'LegacyRouteConverter'
  | 'LegacyRouterURLFormat1'
  | 'LegacyRouterURLFormat2'
  | 'LiveEvent'
  | 'LiveEventCreate'
  | 'LiveEventEdit'
  | 'Logout'
  | 'MediaPostImmersiveView'
  | 'M365CopilotSuggestedContent'
  | 'NetworkAdminSettings'
  | 'NetworkThemeThreads'
  | 'StorylineAnalytics'
  | 'NetworkThemeFeed'
  | 'Questions'
  | 'QuestionsTopicsBrowse'
  | 'QuestionsTopicsFeed'
  | 'QuestionsTopicsFeedWithSubroute'
  | 'QuestionThread'
  | 'RootRedirect'
  | 'Search'
  | 'SearchLegacyRedirect'
  | 'StorylinesHome'
  | 'TeamsMeeting'
  | 'TeamsMeetingDismissed'
  | 'TeamsMeetingInReview'
  | 'TeamsMeetingPublished'
  | 'Thread'
  | 'TopicFeed'
  | 'TopicWithSubroute'
  | 'UsagePolicy'
  | 'UserAchievements'
  | 'UserAudienceFeed'
  | 'UserAudienceSettings'
  | 'UserBookmarks'
  | 'UserDelegationSettings'
  | 'UserDrafts'
  | 'UserFeed'
  | 'UserFeedWithSubroute'
  | 'UserInsightsAnswers'
  | 'UserInsightsPersonal'
  | 'UserPendingPosts'
  | 'UserSettings'
  | 'UserStoryline'
  | 'UserStorylineLegacyRedirect'
  | 'Unknown';

// Component names should be snake_cased for consistency
export enum UiComponentName {
  ArticlePublisher = 'article_publisher',
  DraftThread = 'draft_thread',
  DirectMessagePublisher = 'direct_message_publisher',
  DraftThreadStarter = 'draft_thread_starter',
  EditMessagePublisher = 'edit_message_publisher',
  EventDiscoveryBanners = 'event_discovery_banners',
  EventPublisher = 'event_publisher',
  Feed = 'feed',
  GroupEvents = 'group_events',
  GroupHeader = 'group_header',
  GroupHeaderCompact = 'group_header_compact',
  GroupPublisher = 'group_publisher',
  MediaPost = 'media_post',
  NavigationCommunities = 'navigation_communities',
  NetworkQuestionPublisher = 'network_question_publisher',
  NetworkThemeFeed = 'network_theme_feed',
  NotificationList = 'notification_list',
  PageAskMeAnything = 'page_ask_me_anything',
  PageDiscoverGroups = 'page_discover_groups',
  PageEvent = 'page_event',
  PageGroup = 'page_group',
  PageCampaign = 'page_campaign',
  PageGroupQnaInsights = 'page_group_qna_insights',
  PageGroupTopQuestions = 'page_group_top_questions',
  PageHome = 'page_home',
  PageLogout = 'page_logout',
  PageInbox = 'page_inbox',
  PageNetworkThemes = 'page_network_themes',
  PageCampaignsDashboard = 'page_campaigns_dashboard',
  PageCampaignsHub = 'page_campaigns_hub',
  PageConversationAnalytics = 'page_conversation_analytics',
  PageStorylineAnalytics = 'page_storyline_analytics',
  PageGroupCampaigns = 'page_group_campaigns',
  PageGroupSettings = 'page_group_settings',
  PageTeamsMeeting = 'page_teams_meeting',
  PageStorylineHome = 'page_storyline_home',
  PageAdminSettings = 'page_admin_settings',
  Reply = 'reply',
  ReplyPublisher = 'reply_publisher',
  SearchResults = 'search_results',
  SharePublisher = 'share_publisher',
  SuggestedContentThreadStarter = 'suggested_content_thread_starter',
  TeamsMeetingConfigure = 'teams_meeting_configure',
  TeamsMeetingPublisher = 'teams_meeting_publisher',
  TeamsPageConfigure = 'teams_page_configure',
  TeamsPageUnlicensed = 'teams_page_unlicensed',
  Thread = 'thread',
  ThreadStarter = 'thread_starter',
  ThreadStarterBody = 'thread_starter_body',
  ThreadStarterPublisher = 'thread_starter_publisher',
  TopicHeader = 'topic_header',
  UserHeader = 'user_header',
  UserMomentPublisher = 'user_moment_publisher',
  UserProfilePublisher = 'user_profile_publisher',

  None = 'none',
}

export interface FirstMeaningfulPaintContext {
  readonly firstMeaningfulPaintReported: boolean;
  readonly firstPageLoadReported: boolean;
  readonly firstPageLoadAbandoned: boolean;
  readonly authPageLoadDuration?: number;
}

export interface AcquireTokenPerformanceEventProperties {
  readonly scopes: string;
}

export interface ApiActionPerformanceEventProperties {
  readonly apiName: string;
  readonly actionName?: string;
  readonly actionType?: string;
  readonly cacheExists?: boolean;
  readonly cachingStrategy?: string;
  readonly endedWithError: boolean;
  readonly message?: string;
  readonly reason?: string;
  readonly idempotencyToken?: string;
  readonly requestId: string;
  readonly initiator?: string;
  readonly afdRef?: string;
  readonly cacheHit?: boolean;
}

export interface AzureVideoPlayerPerformanceEventProperties {
  readonly videoId: string;
  readonly groupId?: string;
  readonly errorCode?: string;
}

export interface BootstrapPerformanceEventProperties {
  readonly endedWithError: boolean;
}

export interface FeedUiComponentPerformanceEventProperties extends UiComponentPerformanceEventProperties {
  readonly feedType: string;
}

export interface FileAttachPerformanceEventProperties {
  readonly isSmallFileUpload: boolean;
  readonly numFiles?: number;
}

export interface FilePickerPageLoadPerformanceEventProperties {
  readonly errorCode?: string;
  readonly isExpectedError?: boolean;
  readonly message?: string;
}

export interface FileUploadPerformanceEventProperties {
  readonly storageType: string;
}

export interface FirstMeaningfulPaintPerformanceEventProperties {
  readonly urlRootPath: string;
}

export interface PageLoadPerformanceEventProperties {
  readonly page: PageName;
  readonly path?: string;
  readonly hero: UiComponentName;
  readonly didVisibilityStateChange?: boolean;
  readonly interrupted?: boolean;
  readonly deepLink?: boolean;
}

export interface FirstPaintPerformanceEventProperties {
  readonly isMeasurementApproximated: boolean;
}

export interface ImageLoadPerformanceEventProperties {
  readonly imageOrigin: string;
  readonly imageType: string;
  readonly isGif?: boolean;
  readonly isTenor?: boolean;
  readonly imageSurface?: string;
  readonly isSignedImage?: boolean;
  readonly isPartlyCollapsedAttachmentsLayout?: boolean;
}

export interface NavigationPerformanceEventProperties extends Record<string, number> {
  readonly fetchStart: number;
  readonly responseEnd: number;
  readonly navigationStart: number;
  readonly connectEnd: number;
  readonly connectStart: number;
  readonly domainLookupStart: number;
  readonly domainLookupEnd: number;
  readonly domComplete: number;
  readonly domContentLoadedEventEnd: number;
  readonly domContentLoadedEventStart: number;
  readonly domInteractive: number;
  readonly loadEventEnd: number;
  readonly loadEventStart: number;
  readonly redirectEnd: number;
  readonly redirectStart: number;
  readonly requestStart: number;
  readonly responseStart: number;
  readonly unloadEventEnd: number;
  readonly unloadEventStart: number;
  readonly workerStart: number;
}

export interface PropertyCheckPerformanceEventProperties {
  readonly droppedKeys: string;
}

export interface RenderPerformanceEventProperties {
  readonly endedAfterStyleAndLayout: boolean;
}

type ResourceTimingProperties = Omit<PerformanceResourceTiming, 'toJSON'>;

export interface ResourcePerformanceEventProperties extends ResourceTimingProperties {
  [key: string]: ResourceTimingProperties[keyof ResourceTimingProperties];
  readonly name: string;
}

export interface BundleResourcePerformanceEventProperties extends ResourcePerformanceEventProperties {
  readonly bundleName: string;
}

export interface GraphqlResourcePerformanceEventProperties extends ResourcePerformanceEventProperties {
  readonly operationName: string;
  readonly requestId: string;
}

export interface ScenarioPerformanceEventProperties {
  readonly endedWithError: boolean;
  readonly timedOut: boolean;
  readonly interrupted: boolean;
}

export interface StoriesCarouselPerformanceEventProperties {
  readonly loadTimeStamps?: string[];
  readonly navigationStatus?: string;
  readonly navigationScenario?: string;
  readonly triggerType?: string;
  readonly itemSurface?: string;
  readonly toItemType?: string;
  readonly momentExperienceScenario?: string;
  readonly modalAlreadyOpen?: boolean;
}

export interface TeamsLiveEventVideoPerformanceEventProperties {
  readonly broadcastId?: string;
  readonly type?: string;
}

export interface UiComponentPerformanceEventProperties {
  readonly componentMeasureName: string;
}

export type PerformanceEventProperties =
  | { [key: string]: never }
  | Pick<AcquireTokenPerformanceEventProperties, keyof AcquireTokenPerformanceEventProperties>
  | Pick<ApiActionPerformanceEventProperties, keyof ApiActionPerformanceEventProperties>
  | Pick<AzureVideoPlayerPerformanceEventProperties, keyof AzureVideoPlayerPerformanceEventProperties>
  | Pick<BootstrapPerformanceEventProperties, keyof BootstrapPerformanceEventProperties>
  | Pick<FeedUiComponentPerformanceEventProperties, keyof FeedUiComponentPerformanceEventProperties>
  | Pick<FileAttachPerformanceEventProperties, keyof FileAttachPerformanceEventProperties>
  | Pick<FilePickerPageLoadPerformanceEventProperties, keyof FilePickerPageLoadPerformanceEventProperties>
  | Pick<FileUploadPerformanceEventProperties, keyof FileUploadPerformanceEventProperties>
  | Pick<FirstMeaningfulPaintPerformanceEventProperties, keyof FirstMeaningfulPaintPerformanceEventProperties>
  | Pick<FirstPaintPerformanceEventProperties, keyof FirstPaintPerformanceEventProperties>
  | Pick<ImageLoadPerformanceEventProperties, keyof ImageLoadPerformanceEventProperties>
  | Pick<NavigationPerformanceEventProperties, keyof NavigationPerformanceEventProperties>
  | Pick<PageLoadPerformanceEventProperties, keyof PageLoadPerformanceEventProperties>
  | Pick<PropertyCheckPerformanceEventProperties, keyof PropertyCheckPerformanceEventProperties>
  | Pick<RenderPerformanceEventProperties, keyof RenderPerformanceEventProperties>
  | Pick<ResourcePerformanceEventProperties, keyof ResourcePerformanceEventProperties>
  | Pick<ScenarioPerformanceEventProperties, keyof ScenarioPerformanceEventProperties>
  | Pick<StoriesCarouselPerformanceEventProperties, keyof StoriesCarouselPerformanceEventProperties>
  | Pick<TeamsLiveEventVideoPerformanceEventProperties, keyof TeamsLiveEventVideoPerformanceEventProperties>
  | Pick<UiComponentPerformanceEventProperties, keyof UiComponentPerformanceEventProperties>;

export interface PerformanceMeasure {
  readonly eventName: PerformanceEventName;
  readonly measureKey: string;
  readonly eventProperties?: PerformanceEventProperties;
}

export enum PerformanceEventName {
  AcquireTokenForResource = 'acquire_token_for_resource',
  AmplifyCreateDraft = 'amplify_create_draft',
  AmplifyAttachFiles = 'amplify_attach_files',
  ApiFetch = 'api_fetch',
  AzureVideoLoad = 'azure_video_load',
  AuthBootstrap = 'auth_bootstrap',
  AuthPageLoad = 'auth_page_load',
  Bootstrap = 'bootstrap',
  BootstrapBundleLoad = 'bootstrap_bundle_load',
  BootstrapNavigation = 'bootstrap_navigation',
  CachedFirstPageLoad = 'cached_first_page_load',
  CumulativeLayoutShift = 'cumulative_layout_shift',
  ConvertInteropToContentState = 'convert_interop_to_content_state',
  FileAttach = 'file_attach',
  FileBrowserLoad = 'file_browser_load',
  FilePickerLoad = 'file_picker_load',
  FileUpload = 'file_upload',
  FirstContentfulPaint = 'first_contentful_paint',
  FirstInputDelay = 'first_input_delay',
  FirstMeaningfulPaint = 'first_meaningful_paint',
  FirstPageLoad = 'first_page_load',
  FirstPaint = 'first_paint',
  ImageLoad = 'image_load',
  LargestContentfulPaint = 'largest_contentful_paint',
  LoadTeamsMeetingDetails = 'load_teams_meeting_details',
  ManagedApiAction = 'managed_api_action',
  MessagePost = 'message_post',
  MetaOsHubInitialize = 'meta_os_hub_initialize',
  MomentHomeCarouselLoad = 'moment_home_carousel_load',
  MomentsNavigation = 'moments_navigation',
  MomentUserCarouselLoad = 'moment_user_carousel_load',
  PageLoad = 'page_load',
  PerceivedImageLoad = 'perceived_image_load',
  RendererBundleLoad = 'renderer_bundle_load',
  ResourceTiming = 'resource_timing',
  ServiceWorkerRegistration = 'service_worker_registration',
  ServiceWorkerDisable = 'service_worker_disable',
  SharePointAuthTokenFetch = 'sharepoint_auth_token_fetch',
  SharePointWebPartConfiguration = 'webpart_config',
  TeamsLiveEventVideoLoad = 'teams_liveevent_video_load',
  TimeToFirstByte = 'time_to_first_byte',
  UiComponentMeasure = 'ui_component_measure',
}

export interface ScenarioPerformanceData {
  readonly timer: number;
  readonly measure: PerformanceMeasure;
}

export type ScenarioCollection = Record<string, ScenarioPerformanceData>;

export type RequestInitiator =
  | 'realtime'
  | 'realtime_retry'
  | 'realtime_notification'
  | 'bootstrap'
  | 'bootstrap_async'
  | 'auth_bootstrap';
